import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api, { getToken } from "@/lib/api";
import { ScoreCards } from "@/components/ScoreCards";
import { MessagesSection } from "@/components/MessageThread";
import { DocumentRequests } from "@/components/DocumentRequests";
import { SecureDeliveryNote } from "@/components/EnterpriseCrm";
import RetainerSignCard from "@/components/RetainerSignCard";
import DroneRequestCard from "@/components/DroneRequestCard";
import TelehealthConsentCard from "@/components/TelehealthConsentCard";
import { EvidenceIntelBadge } from "@/components/EvidenceIntelligence";
import {
  ChevronLeft, Camera, Clock, Car, Users, BadgeCheck, Stethoscope, FileCheck2,
  Scale, Edit3, Loader2, Phone, ShieldCheck, Image as ImageIcon, Film, Mic, FolderDown, Sparkles,
} from "lucide-react";

const KIND_ICON = { accident: ShieldCheck, photo: Camera, medical: Stethoscope, insurance: FileCheck2, attorney: Scale, status: BadgeCheck };
const MEDIA_ICON = { image: ImageIcon, video: Film, audio: Mic };

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">{title}</h3>
      </div>
      {children}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between py-2 border-b border-slate-50 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="text-navy font-medium text-right">{v || "—"}</span>
  </div>
);

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}`); setData(data); }
    catch { toast.error("Could not load case"); navigate("/dashboard"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  if (!data) return <AppLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div></AppLayout>;

  const token = getToken();

  return (
    <AppLayout>
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-navy mb-5"><ChevronLeft className="w-4 h-4" /> Back to dashboard</Link>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${data.status === "active" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{data.status}</span>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-navy mt-2">{data.title}</h1>
          {data.case_number && <p className="text-xs font-mono text-cobalt mt-1" data-testid="case-number">{data.case_number}</p>}
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {new Date(data.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(`/new-accident?case=${caseId}`)} data-testid="edit-case-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all"><Edit3 className="w-4 h-4" /> Edit</button>
          <button onClick={() => navigate(`/cases/${caseId}/score`)} data-testid="score-case-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all"><Sparkles className="w-4 h-4" /> {data.scores ? "View Score" : "Score Case"}</button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/export?auth=${token}`} data-testid="download-case-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all"><FolderDown className="w-4 h-4" /> Case File</a>
          {(data.referral_status && data.referral_status !== "none") && (
            <span className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt-light text-cobalt font-medium" data-testid="referral-status-badge"><Scale className="w-4 h-4" /> {data.referral_status === "marketplace" ? "Listed on Marketplace" : data.claimed_by ? "Attorney Assigned" : `Referral ${data.referral_status}`}</span>
          )}
        </div>
      </div>

      <div className="mb-6" data-testid="retainer-slot">
        <RetainerSignCard caseId={caseId} clientName={data.driver?.name} onSigned={load} />
      </div>

      <div className="mb-6">
        <DroneRequestCard caseId={caseId} />
      </div>

      <div className="mb-6">
        <TelehealthConsentCard caseId={caseId} />
      </div>

      {data.scores && (
        <div className="mb-6" data-testid="case-score-cards">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-cobalt" />
            <h2 className="font-heading text-xl font-bold text-navy">Crash Ready Score</h2>
          </div>
          <ScoreCards scores={data.scores} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          <Section icon={Car} title="Driver Information">
            <Row k="Name" v={data.driver?.name} /><Row k="Phone" v={data.driver?.phone} /><Row k="Email" v={data.driver?.email} />
            <Row k="Insurance" v={data.driver?.insurance_company} /><Row k="Policy #" v={data.driver?.policy_number} /><Row k="Vehicle" v={data.driver?.vehicle} />
          </Section>

          <Section icon={Camera} title={`Evidence (${data.evidence?.length || 0})`}>
            <div className="mb-3"><EvidenceIntelBadge caseId={data.case_id} /></div>
            {data.evidence?.length ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {data.evidence.map((ev) => {
                  const MI = MEDIA_ICON[ev.media_type] || ImageIcon;
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/evidence/${ev.evidence_id}/download?auth=${token}`;
                  return (
                    <a key={ev.evidence_id} href={url} target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden border border-slate-100 aspect-square bg-slate-50 hover:opacity-90">
                      {ev.media_type === "image" ? <img src={url} alt={ev.original_filename} className="w-full h-full object-cover" /> : <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1 text-xs"><MI className="w-6 h-6" />{ev.media_type}</div>}
                      <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1.5 py-0.5 truncate">{ev.folder}</span>
                    </a>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400">No evidence uploaded yet.</p>}
          </Section>

          {data.witnesses?.length > 0 && (
            <Section icon={Users} title={`Witnesses (${data.witnesses.length})`}>
              {data.witnesses.map((w, i) => (
                <div key={i} className="py-2 border-b border-slate-50 last:border-0">
                  <div className="font-medium text-navy text-sm">{w.name || "Unnamed"}</div>
                  <div className="text-xs text-slate-500">{[w.phone, w.email].filter(Boolean).join(" · ")}</div>
                </div>
              ))}
            </Section>
          )}

          {data.medical?.length > 0 && (
            <Section icon={Stethoscope} title="Medical Treatment">
              {data.medical.map((m, i) => <Row key={i} k={m.type || "Treatment"} v={[m.provider, m.date].filter(Boolean).join(" · ")} />)}
            </Section>
          )}

          <Section icon={BadgeCheck} title="Police Report">
            <Row k="Officer" v={data.police?.officer_name} /><Row k="Badge" v={data.police?.badge} /><Row k="Agency" v={data.police?.agency} /><Row k="Report #" v={data.police?.report_number} />
          </Section>

          <Section icon={FileCheck2} title="Insurance">
            <Row k="Claim #" v={data.insurance?.claim_number} /><Row k="Adjuster" v={data.insurance?.adjuster} /><Row k="Phone" v={data.insurance?.phone} /><Row k="Total loss" v={data.insurance?.total_loss} />
          </Section>

          {data.claimed_by && <SecureDeliveryNote attorneyId={data.claimed_by} />}
          {data.claimed_by && <DocumentRequests caseId={caseId} isAttorney={false} />}
          {data.claimed_by && <MessagesSection caseId={caseId} />}
        </div>

        {/* Right: timeline */}
        <div>
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sticky top-24">
            <div className="flex items-center gap-2 mb-5"><Clock className="w-5 h-5 text-cobalt" /><h3 className="font-heading font-semibold text-navy">Timeline</h3></div>
            <div className="space-y-0">
              {data.timeline?.map((e, i) => {
                const Icon = KIND_ICON[e.kind] || Clock;
                return (
                  <div key={e.event_id} className="flex gap-3" data-testid={`timeline-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="h-8 w-8 rounded-full bg-cobalt-light flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-cobalt" /></div>
                      {i < data.timeline.length - 1 && <div className="w-px flex-1 bg-slate-100 my-1" />}
                    </div>
                    <div className="pb-5">
                      <div className="text-sm font-medium text-navy">{e.title}</div>
                      <div className="text-xs text-slate-400">
                        {new Date(e.occurred_at).toLocaleString()}
                        {e.actor_name && <span className="text-slate-500"> · {e.actor_name}{e.actor_role ? ` (${e.actor_role})` : ""}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <a href="tel:911" className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-3.5 rounded-full shadow-xl font-semibold transition-all active:scale-95">
        <Phone className="w-5 h-5" /> Call 911
      </a>
    </AppLayout>
  );
}
